<template>
    <div class="login-container">
        <div class="form-side">
            <!-- Primeira tela de login -->
            <div class="login-box" v-if="!showCompanySelection">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <form @submit.prevent="handleLogin">
                    <div class="form-group">
                        <label>{{ $t('auth.email') }}</label>
                        <input 
                            type="email"
                            v-model="email"
                            :placeholder="$t('auth.emailPlaceholder')"
                            required
                        />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('auth.password') }}</label>
                        <div class="password-input">
                            <input 
                                :type="showPassword ? 'text' : 'password'"
                                v-model="password"
                                :placeholder="$t('auth.passwordPlaceholder')"
                                required
                            />
                            <span class="password-toggle" @click="togglePassword">
                                <fa :icon="showPassword ? 'eye-slash' : 'eye'" />
                            </span>
                        </div>
                    </div>
                    <div v-if="loginError" class="login-error">
                        {{ $t('auth.incorrectCredentials') }} <a href="/esqueci-senha-portal">{{ $t('auth.resetYourPassword') }}</a>.
                    </div>
                    <div class="links">
                        <a href="/esqueci-senha-portal">{{ $t('auth.forgotPassword') }}</a>
                    </div>
                    <button type="submit" class="btn-login">{{ $t('auth.login') }}</button>
                </form>
            </div>

            <!-- Segunda tela - Seleção de empresa -->
            <div class="login-box" v-if="showCompanySelection">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <div class="company-selection">
                    <h3>{{ $t('auth.selectACompany') }}</h3>
                    <p>{{ $t('auth.multipleCompanies') }}</p>
                    
                    <div class="form-group">
                        <label>{{ $t('auth.company') }}</label>
                        <select v-model="selectedCompany" required>
                            <option value="0">{{ $t('auth.selectACompany') }}</option>
                            <option 
                                v-for="website in availableWebsites" 
                                :key="website.id" 
                                :value="website"
                            >
                                {{ website.nome }}
                            </option>
                        </select>
                    </div>

                    <div class="company-actions">
                        <button type="button" class="btn-back" @click="goBackToLogin">
                            {{ $t('common.back') }}
                        </button>
                        <button 
                            type="button" 
                            class="btn-login" 
                            @click="proceedWithSelectedCompany"
                            :disabled="!selectedCompany || selectedCompany === 0"
                        >
                            {{ $t('auth.login') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="banner-side">
            <div class="banner-content">
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import userApi from '@/utils/user.js';
import loginApi from '@/utils/login.js';
import { syncAdminModeFromTypeBusiness } from '@/utils/corporate/adminDashboardMode';

export default {
    data() {
        return {
            email: '',
            password: '',
            token: '',
            refreshToken: '',
            typeIdLocal: null,
            idUser: null,
            websites: null,
            darkMode: false,
            typeSystemLocal: null,
            lististes: [],
            showPassword: false,
            loginError: false,
            showCompanySelection: false,
            availableWebsites: [],
            selectedCompany: 0,
            loginResponse: null,
        }
    },
    methods: {
        ...mapActions([
            'storeTypeSystem',
            'toggleDarkMode', 
            'storeToken',
            'storeRefreshToken',
            'storeTypeId',
            'storeWebsites',
            'storeUser',
        ]),
        togglePassword() {
            this.showPassword = !this.showPassword;
        },
        async handleLogin() {
            this.loginError = false;
            await this.checkUserCompanies();
        },
        async checkUserCompanies() {
            try {
                // Primeira chamada sem domain para verificar se há múltiplas empresas
                const response = await loginApi.token({
                    "email": this.email,
                    "password": this.password
                }, {
                    withCredentials: false,
                });

                this.loginResponse = response.data;

                // Verifica se há múltiplas empresas na resposta
                if (response.data.listWebsite && response.data.listWebsite.length > 1) {
                    this.availableWebsites = response.data.listWebsite;
                    this.showCompanySelection = true;
                } else if (response.data.listWebsite && response.data.listWebsite.length === 1) {
                    // Se há apenas uma empresa, faz nova chamada com a URL da empresa
                    const selectedWebsite = response.data.listWebsite[0];
                    await this.loginWithCompanyUrl(selectedWebsite);
                } else {
                    // Se não há listWebsite, faz login normal com domain
                    let domainUrl = window.location.origin + window.location.pathname.split('/portal')[0];
                    if (window.location.hostname === 'localhost') {
                        domainUrl = process.env.VUE_APP_BASE_URL_HOMOLOGATION;
                    }
                    
                    const domainResponse = await loginApi.token({
                        "email": this.email,
                        "password": this.password,
                        "domain": domainUrl
                    }, {
                        withCredentials: false,
                    });

                    await this.completeLogin(domainResponse.data);
                }

            } catch (error) {
                console.error(error,'Erro ao buscar token');
                this.loginError = true;
            } 
        },
        async loginWithCompanyUrl(selectedWebsite) {
            try {
                // Faz nova chamada de login com o domínio da empresa selecionada
                const response = await loginApi.token({
                    "email": this.email,
                    "password": this.password,
                    "domain": selectedWebsite.url
                }, {
                    withCredentials: false,
                });

                this.websites = selectedWebsite;
                await this.completeLogin(response.data);

            } catch (error) {
                console.error(error,'Erro ao fazer login com empresa selecionada');
                this.loginError = true;
            }
        },
        async proceedWithSelectedCompany() {
            if (!this.selectedCompany || this.selectedCompany === 0) return;

            await this.loginWithCompanyUrl(this.selectedCompany);
        },
        persistLoginWebsite(website) {
            this.websites = website || null;
            this.storeWebsites({ websites: this.websites });
            // typeBusiness 2 → corporativo; 1 → escolar (sobrescreve company persistida)
            syncAdminModeFromTypeBusiness(this.websites);
        },
        async completeLogin(loginData) {
            this.token = loginData.acessToken;
            this.typeIdLocal = loginData.type;
            this.refreshToken = loginData.refreshToken;
            this.idUser = loginData.user.id;
            if (!this.websites && loginData.listWebsite && loginData.listWebsite[0]) {
                this.websites = loginData.listWebsite[0];
            }
            this.darkMode = this.websites && this.websites.typeLayout == 2;
            this.typeSystemLocal = loginData.typeSystem;

            this.storeTypeSystem({typeSystem: loginData.typeSystem});
            this.toggleDarkMode({dark: this.darkMode});
            this.setItemStorage();
            this.storeToken({token: this.token})
            this.storeRefreshToken({refreshToken: this.refreshToken})
            this.storeTypeId({typeId: this.typeIdLocal})
            this.fetchUserData(this.idUser, this.token);
            this.persistLoginWebsite(this.websites);
            await this.getLogo();
            this.$router.push('/aluno/dashboard');
        },
        goBackToLogin() {
            this.showCompanySelection = false;
            this.selectedCompany = 0;
            this.availableWebsites = [];
            this.loginResponse = null;
        },
        async getTokenManyCompany() {
            try {
                let domainUrl = window.location.origin + window.location.pathname.split('/portal')[0];
                if (window.location.hostname === 'localhost') {
                    domainUrl = process.env.VUE_APP_BASE_URL_HOMOLOGATION;
                }
                const response = await loginApi.token({
                    "email": this.email,
                    "password": this.password,
                    "domain": domainUrl
                }, {
                    withCredentials: false,
                });
                this.token = response.data.acessToken;
                this.typeIdLocal = response.data.type;
                this.refreshToken = response.data.refreshToken;
                this.idUser = response.data.user.id;
                this.persistLoginWebsite(response.data.listWebsite[0]);
                this.darkMode = this.websites && this.websites.typeLayout == 2;
                this.typeSystemLocal = response.data.typeSystem;
                this.storeTypeSystem({typeSystem:response.data.typeSystem});
                this.toggleDarkMode({dark:this.darkMode});
                this.setItemStorage();
                this.storeToken({token: this.token})
                this.storeRefreshToken({refreshToken: this.refreshToken})
                this.storeTypeId({typeId: this.typeIdLocal})
                this.fetchUserData(this.idUser, this.token);
                await this.getLogo();
                this.$router.push('/aluno/dashboard');

            } catch (error) {
                console.error(error,'Erro ao buscar token');
                this.loginError = true;
            } 
        },
        setItemStorage() {
            localStorage.setItem('logado', 'true');
        },
        async fetchUserData(id, tokenAPI) {
            try {
                const userData = await userApi.getDataUserPortal(tokenAPI);
                this.storeUser({user: userData});
            } catch (error) {
                console.error(error,'Erro ao buscar dados do usuário');
            }
        },
        async getLogo() {
            if(window.location.hostname == 'localhost'){
                this.logo = 'https://image.weeduc.com/logo.png';
            }else{
                const aux = window.location.origin.replace(/\/$/, '');
                const url = aux.split('/portal')[0];
                const obj = { url: url };

                try {
                    const response = await loginApi.logo(obj);
                    this.logo = 'https://image.weeduc.com/'+response.logo;
                    if (!this.token) {
                        this.typeSystemLocal = response.typeSystem;
                    }
                    
                } catch (error) {
                    console.error(error,'Erro ao buscar logo');
                }
            }
        }
    }
}
</script>

<style scoped>
.login-container {
    display: flex;
    min-height: 100vh;
}

.banner-side {
    flex: 1 1 0;
    min-width: 0;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    position: relative;
}

.banner-content {
    position: relative;
    text-align: center;
    padding: 1.25rem;
}

.banner-content h1 {
    font-size: 2.5em;
    margin-bottom: 0.9375rem;
}

.form-side {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
    padding: 2.5rem;
    box-sizing: border-box;
    flex: 0 0 30rem;
    width: 30rem;
}

.login-box {
    width: 100%;
    max-width: 25rem;
    background: white;
    padding: 2.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.625rem rgba(0,0,0,0.1);
}

.logo {
    text-align: center;
    margin-bottom: 1.875rem;
}

.logo img {
    width: 12.5rem;
}

h2 {
    text-align: center;
    color: #272f3a;
    margin-bottom: 1.875rem;
}

.company-selection {
    text-align: center;
}

.company-selection h3 {
    color: #272f3a;
    margin-bottom: 1rem;
    font-size: 1.5rem;
}

.company-selection p {
    color: #757e8a;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    line-height: 1.4;
}

.form-group {
    margin-bottom: 1.25rem;
}

label {
    display: block;
    margin-bottom: 0.5rem;
    color: #757e8a;
    text-align: left;
}

input, select {
    width: 100%;
    padding: 0.75rem;
    border: 0.0625rem solid #ddd;
    border-radius: 0.25rem;
    font-size: 0.875rem;
}

select {
    background-color: white;
    cursor: pointer;
}

.password-input {
    position: relative;
    display: flex;
    align-items: center;
}

.password-toggle {
    position: absolute;
    right: 0.75rem;
    cursor: pointer;
    color: #757e8a;
}

.login-error {
    color: #d32f2f;
    background: #fff0f0;
    border: 1px solid #d32f2f;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 10px;
    font-size: 0.95em;
    margin: 15px;
    text-align: center;
}

.login-error a {
    color: #3db8bf;
    text-decoration: underline;
}

.btn-login {
    width: 93%;
    padding: 0.75rem;
    background-color: #3db8bf;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
    margin: 10px;
}

.btn-login:hover {
    background-color: #2a9ca3;
}

.btn-login:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.btn-back {
    width: 40%;
    padding: 0.75rem;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
    margin-right: 10px;
}

.btn-back:hover {
    background-color: #5a6268;
}

.company-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.5rem;
}

.company-actions .btn-login {
    width: 55%;
    margin: 0;
}

.links {
    margin-top: 10px;
    text-align: center;
    display: flex;
    justify-content: end;
    padding: 0px 13px;
}

.links a {
    color: #3db8bf;
    text-decoration: none;
    font-size: 12px;
}

.links a:hover {
    color: #333;
}

@media (max-width: 48rem) {
    .login-container {
        flex-direction: column;
    }
    
    .banner-side {
        display: none;
    }
    
    .form-side {
        padding: 1.25rem;
    }
    
    .login-box {
        padding: 1.25rem;
    }

    .company-actions {
        flex-direction: column;
        gap: 10px;
    }

    .company-actions .btn-login,
    .btn-back {
        width: 100%;
        margin: 0;
    }
}
.banner-side {
    background-image: url('@/assets/portal/Login-weeduc.png');
    background-size: cover;
    background-position: center;
    background-color: none!important;
}
@media (max-width: 768px) {
    .form-side {
        flex: 1 1 auto;
        width: 100%;
        height: 100vh;
    }
}
</style>
